import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Users,
  Shield,
  UserCog,
  Pencil, 
  Trash2,
  Eye,
  EyeOff,
  CheckCircle,
  XCircle,
  Copy,
  RefreshCw,
  Mail,
  Key
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter,
  DialogDescription
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import api from '../lib/api';
import { formatDate } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { CollaboratorProfileModal } from '../components/CollaboratorProfileModal';
import { IdCard } from 'lucide-react';

// Generate random password
const generatePassword = () => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789!@#$%';
  let password = '';
  for (let i = 0; i < 10; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
};

const roleLabels = {
  admin: { label: 'Administrateur', color: 'bg-purple-100 text-purple-800', icon: Shield },
  agent: { label: 'Agent', color: 'bg-blue-100 text-blue-800', icon: UserCog }
};

const UserManagement = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isCredentialsOpen, setIsCredentialsOpen] = useState(false);
  const [profileUserId, setProfileUserId] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Form data
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: 'agent',
    employee_id: '',
    manager_id: '',
  });
  
  // Credentials to show after creation
  const [newCredentials, setNewCredentials] = useState(null);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/users');
      setUsers(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenForm = (user = null) => {
    if (user) {
      setSelectedUser(user);
      setFormData({
        email: user.email,
        name: user.name,
        password: '',
        role: user.role,
        employee_id: user.employee_id || '',
        manager_id: user.manager_id || '',
      });
    } else {
      setSelectedUser(null);
      setFormData({
        email: '',
        name: '',
        password: generatePassword(),
        role: 'agent',
        employee_id: '',
        manager_id: '',
      });
    }
    setShowPassword(false);
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedUser(null);
    setFormData({
      email: '',
      name: '',
      password: '',
      role: 'agent',
      employee_id: '',
      manager_id: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.email || !formData.name) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    if (!selectedUser && !formData.password) {
      toast.error('Veuillez définir un mot de passe');
      return;
    }

    setSaving(true);
    try {
      if (selectedUser) {
        // Update
        const updateData = {
          name: formData.name,
          role: formData.role,
        };
        if (formData.password) {
          updateData.password = formData.password;
        }
        if (formData.employee_id) {
          updateData.employee_id = formData.employee_id;
        }
        // Manager assignment — envoyé uniquement par le super_admin
        if (currentUser?.role === 'super_admin') {
          updateData.manager_id = formData.manager_id || null;
        }
        await api.put(`/users/${selectedUser.id}`, updateData);
        toast.success('Utilisateur mis à jour');
        handleCloseForm();
      } else {
        // Create
        const response = await api.post('/users', {
          ...formData,
          employee_id: formData.employee_id || null,
        });
        
        // Show credentials
        setNewCredentials({
          email: formData.email,
          password: formData.password,
          name: formData.name,
          employee_id: response.data.employee_id,
        });
        setIsCredentialsOpen(true);
        
        handleCloseForm();
        toast.success('Utilisateur créé avec succès');
      }
      
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'enregistrement';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (user) => {
    if (user.id === currentUser?.id) {
      toast.error('Vous ne pouvez pas désactiver votre propre compte');
      return;
    }
    
    try {
      await api.put(`/users/${user.id}`, {
        is_active: !user.is_active
      });
      toast.success(user.is_active ? 'Compte désactivé' : 'Compte activé');
      fetchUsers();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const handleDelete = async () => {
    if (!selectedUser) return;
    
    try {
      await api.delete(`/users/${selectedUser.id}`);
      toast.success('Utilisateur supprimé');
      setIsDeleteOpen(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la suppression';
      toast.error(message);
    }
  };

  const handleResetPassword = async (user) => {
    const newPassword = generatePassword();
    try {
      await api.put(`/users/${user.id}`, {
        password: newPassword
      });
      
      setNewCredentials({
        email: user.email,
        password: newPassword,
        name: user.name,
        employee_id: user.employee_id,
      });
      setIsCredentialsOpen(true);
      
      toast.success('Mot de passe réinitialisé');
    } catch (error) {
      toast.error('Erreur lors de la réinitialisation');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copié dans le presse-papiers');
  };

  const filteredUsers = users.filter(user => {
    const searchLower = searchQuery.toLowerCase();
    return (
      user.email?.toLowerCase().includes(searchLower) ||
      user.name?.toLowerCase().includes(searchLower) ||
      user.employee_id?.toLowerCase().includes(searchLower)
    );
  });

  const adminCount = users.filter(u => u.role === 'admin').length;
  const agentCount = users.filter(u => u.role === 'agent').length;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  if (currentUser?.role === 'agent') {
    return (
      <div className="flex items-center justify-center h-64" data-testid="users-admin-only">
        <p className="text-gray-500">Gestion des utilisateurs réservée aux administrateurs</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="user-management-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Utilisateurs</h1>
          <p className="text-gray-500">{adminCount} admin(s), {agentCount} agent(s)</p>
        </div>
        <Button 
          onClick={() => handleOpenForm()}
          className="bg-[#1B3A4B] hover:bg-[#142D3A]"
          data-testid="add-user-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouvel utilisateur
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Shield className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Administrateurs</p>
                <p className="text-xl font-bold">{adminCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <UserCog className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Agents</p>
                <p className="text-xl font-bold">{agentCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total</p>
                <p className="text-xl font-bold">{users.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Rechercher par nom, email ou identifiant..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="search-users"
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Utilisateur</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Identifiant</TableHead>
                <TableHead>Rôle</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.length > 0 ? (
                filteredUsers.map((user) => {
                  const RoleIcon = roleLabels[user.role]?.icon || UserCog;
                  const isCurrentUser = user.id === currentUser?.id;
                  
                  return (
                    <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-[#D4A373] flex items-center justify-center">
                            <span className="text-white font-medium text-sm">
                              {user.name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium">{user.name}</p>
                            {isCurrentUser && (
                              <span className="text-xs text-[#1B3A4B] font-medium">(Vous)</span>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4 text-gray-400" />
                          <span className="text-sm">{user.email}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-sm font-medium text-[#1B3A4B]">
                          {user.employee_id}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge className={roleLabels[user.role]?.color}>
                          <RoleIcon className="w-3 h-3 mr-1" />
                          {roleLabels[user.role]?.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          className={user.is_active !== false
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                          }
                        >
                          {user.is_active !== false ? 'Actif' : 'Inactif'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setProfileUserId(user.id)}
                            title="Fiche collaborateur"
                            data-testid={`open-profile-${user.id}`}
                          >
                            <IdCard className="w-4 h-4 text-[#1B3A4B]" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleResetPassword(user)}
                            title="Réinitialiser le mot de passe"
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                          {!isCurrentUser && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleActive(user)}
                              title={user.is_active !== false ? 'Désactiver' : 'Activer'}
                            >
                              {user.is_active !== false ? (
                                <XCircle className="w-4 h-4 text-red-500" />
                              ) : (
                                <CheckCircle className="w-4 h-4 text-green-500" />
                              )}
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleOpenForm(user)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          {!isCurrentUser && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedUser(user);
                                setIsDeleteOpen(true);
                              }}
                            >
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">
                    <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-gray-500">
                      {searchQuery ? 'Aucun utilisateur trouvé' : 'Aucun utilisateur'}
                    </p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create/Edit Form Dialog */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {selectedUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
            </DialogTitle>
            <DialogDescription>
              {selectedUser 
                ? 'Modifiez les informations de l\'utilisateur'
                : 'Créez un nouveau compte administrateur ou agent'
              }
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="name">Nom complet *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Jean Dupont"
                data-testid="user-name-input"
              />
            </div>

            {!selectedUser && (
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="jean.dupont@immogabs.ml"
                  data-testid="user-email-input"
                />
              </div>
            )}

            <div>
              <Label htmlFor="role">Rôle *</Label>
              <Select
                value={formData.role}
                onValueChange={(value) => setFormData({ ...formData, role: value })}
              >
                <SelectTrigger data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-purple-600" />
                      Administrateur
                    </div>
                  </SelectItem>
                  <SelectItem value="agent">
                    <div className="flex items-center gap-2">
                      <UserCog className="w-4 h-4 text-blue-600" />
                      Agent
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1">
                {formData.role === 'admin' 
                  ? 'Accès complet à toutes les fonctionnalités' 
                  : 'Accès limité aux opérations courantes'
                }
              </p>
            </div>

            <div>
              <Label htmlFor="employee_id">Identifiant personnalisé</Label>
              <Input
                id="employee_id"
                value={formData.employee_id}
                onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                placeholder={selectedUser?.employee_id || "Ex: IMMO-AGENT01"}
                data-testid="user-employee-id-input"
              />
              <p className="text-xs text-gray-500 mt-1">
                Laissez vide pour générer automatiquement (IMMO-XXXXXX)
              </p>
            </div>

            {/* Manager assignment (Super-Admin uniquement, sur édition) */}
            {selectedUser && currentUser?.role === 'super_admin' && (
              <div>
                <Label htmlFor="manager_id">Manager (validateur des congés)</Label>
                <select
                  id="manager_id"
                  value={formData.manager_id}
                  onChange={(e) => setFormData({ ...formData, manager_id: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
                  data-testid="user-manager-select"
                >
                  <option value="">— Aucun manager désigné —</option>
                  {users.filter((u) => u.id !== selectedUser.id).map((u) => (
                    <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Le manager approuvera les demandes de congés et journées en avance du collaborateur.
                </p>
              </div>
            )}

            <div>
              <Label htmlFor="password">
                {selectedUser ? 'Nouveau mot de passe' : 'Mot de passe *'}
              </Label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder={selectedUser ? "Laisser vide pour ne pas modifier" : "Mot de passe"}
                    data-testid="user-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setFormData({ ...formData, password: generatePassword() })}
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseForm}>
                Annuler
              </Button>
              <Button 
                type="submit" 
                disabled={saving}
                className="bg-[#1B3A4B] hover:bg-[#142D3A]"
                data-testid="submit-user-btn"
              >
                {saving ? 'Enregistrement...' : selectedUser ? 'Modifier' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Credentials Display Dialog */}
      <Dialog open={isCredentialsOpen} onOpenChange={setIsCredentialsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              Identifiants créés
            </DialogTitle>
            <DialogDescription>
              Communiquez ces identifiants à l'utilisateur. Le mot de passe ne sera plus visible.
            </DialogDescription>
          </DialogHeader>
          
          {newCredentials && (
            <div className="space-y-4">
              <Card className="bg-gray-50">
                <CardContent className="p-4 space-y-3">
                  <div>
                    <p className="text-sm text-gray-500">Nom</p>
                    <p className="font-medium">{newCredentials.name}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Identifiant collaborateur</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-white px-3 py-2 rounded border font-mono text-[#1B3A4B] font-bold">
                        {newCredentials.employee_id}
                      </code>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(newCredentials.employee_id)}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-white px-3 py-2 rounded border font-mono">
                        {newCredentials.email}
                      </code>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(newCredentials.email)}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  <div>
                    <p className="text-sm text-gray-500">Mot de passe</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 bg-white px-3 py-2 rounded border font-mono">
                        {newCredentials.password}
                      </code>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyToClipboard(newCredentials.password)}
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-800">
                <p className="font-medium mb-1">Instructions:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Aller sur la page de connexion</li>
                  <li>Entrer l'email et le mot de passe</li>
                  <li>Recommander de changer le mot de passe après première connexion</li>
                </ol>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                if (newCredentials) {
                  const text = `Identifiant: ${newCredentials.employee_id}\nEmail: ${newCredentials.email}\nMot de passe: ${newCredentials.password}`;
                  copyToClipboard(text);
                }
              }}
            >
              <Copy className="w-4 h-4 mr-2" />
              Copier tout
            </Button>
            <Button 
              onClick={() => setIsCredentialsOpen(false)}
              className="bg-[#1B3A4B] hover:bg-[#142D3A]"
            >
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet utilisateur ?</AlertDialogTitle>
            <AlertDialogDescription>
              L'utilisateur "{selectedUser?.name}" ({selectedUser?.employee_id}) ne pourra plus accéder à l'application.
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Collaborator Profile Modal */}
      <CollaboratorProfileModal
        open={!!profileUserId}
        userId={profileUserId}
        onClose={() => setProfileUserId(null)}
        onUpdated={fetchUsers}
      />
    </div>
  );
};

export default UserManagement;
